import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import { Modal, Input, Button, Space, Empty, Spin, Typography } from 'antd'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  HolderOutlined,
  PlusOutlined,
  DeleteOutlined,
  SearchOutlined,
  ArrowRightOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons'
import { SortableItem, SortableItemsSelectorProps } from './interfaces'
import {
  ModalContent,
  ItemList,
  ListHeader,
  HeaderTitle,
  HeaderCounter,
  ListBody,
  ItemCard,
  DragHandle,
  ItemContent,
  ItemTitle,
  ItemDescription,
  ActionButtons,
  SortOrderBadge,
  SearchWrapper,
} from './styles'

const { Search } = Input
const { Text } = Typography

interface SortableItemComponentProps {
  item: SortableItem
  index: number
  onRemove?: () => void
}

const SortableItemComponent: React.FC<SortableItemComponentProps> = ({ item, index, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.key,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <ItemCard $isDragging={isDragging}>
        <DragHandle>
          <HolderOutlined />
        </DragHandle>
        <SortOrderBadge color="blue">{index + 1}</SortOrderBadge>
        <ItemContent>
          <ItemTitle>{item.title}</ItemTitle>
          {item.description && <ItemDescription>{item.description}</ItemDescription>}
        </ItemContent>
        {onRemove && (
          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={(e) => {
              e.stopPropagation()
              onRemove()
            }}
            onPointerDown={(e) => e.stopPropagation()}
            title="Удалить"
          />
        )}
      </ItemCard>
    </div>
  )
}

interface AvailableItemComponentProps {
  item: SortableItem
  onAdd: () => void
}

const AvailableItemComponent: React.FC<AvailableItemComponentProps> = ({ item, onAdd }) => {
  return (
    <ItemCard $isAvailable onClick={onAdd} onDoubleClick={onAdd} title="Нажмите для добавления">
      <ItemContent>
        <ItemTitle>{item.title}</ItemTitle>
        {item.description && <ItemDescription>{item.description}</ItemDescription>}
      </ItemContent>
      <Button
        type="text"
        size="small"
        icon={<PlusOutlined />}
        onClick={e => {
          e.stopPropagation()
          onAdd()
        }}
        title="Добавить"
      />
    </ItemCard>
  )
}

export const SortableItemsSelector: React.FC<SortableItemsSelectorProps> = ({
  visible,
  title,
  availableItems,
  selectedItemKeys,
  loading,
  leftPanelTitle = 'Доступные элементы',
  rightPanelTitle = 'Выбранные элементы',
  emptyLeftText = 'Нет доступных элементов',
  emptyRightText = 'Добавьте элементы из списка слева',
  emptyRightDescription = 'Нажмите на элемент или используйте кнопку +',
  showSearch = true,
  width = 900,
  onOk,
  onCancel,
}) => {
  const [localSelectedKeys, setLocalSelectedKeys] = useState<string[]>(selectedItemKeys)
  const [activeId, setActiveId] = useState<string | null>(null)
  const [searchAvailable, setSearchAvailable] = useState('')
  const [searchSelected, setSearchSelected] = useState('')
  const prevVisibleRef = useRef(visible)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const allItemsMap = useMemo(() => {
    const map = new Map<string, SortableItem>()
    availableItems.forEach(item => {
      map.set(item.key, item)
    })
    return map
  }, [availableItems])

  const availableItemsFiltered = useMemo(() => {
    const available = availableItems.filter(item => !localSelectedKeys.includes(item.key))
    if (!showSearch || !searchAvailable) return available
    const searchLower = searchAvailable.toLowerCase()
    return available.filter(item => {
      const titleMatch =
        typeof item.title === 'string' && item.title.toLowerCase().includes(searchLower)
      const descriptionMatch =
        item.description && item.description.toLowerCase().includes(searchLower)
      return titleMatch || descriptionMatch
    })
  }, [availableItems, localSelectedKeys, searchAvailable, showSearch])

  const selectedItems = useMemo(() => {
    const items = localSelectedKeys
      .map(key => allItemsMap.get(key))
      .filter(Boolean) as SortableItem[]

    if (!showSearch || !searchSelected) return items
    const searchLower = searchSelected.toLowerCase()
    return items.filter(item => {
      const titleMatch =
        typeof item.title === 'string' && item.title.toLowerCase().includes(searchLower)
      const descriptionMatch =
        item.description && item.description.toLowerCase().includes(searchLower)
      return titleMatch || descriptionMatch
    })
  }, [localSelectedKeys, allItemsMap, searchSelected, showSearch])

  const handleAddItem = useCallback((itemKey: string) => {
    setLocalSelectedKeys(prev => [...prev, itemKey])
  }, [])

  const handleRemoveItem = useCallback((itemKey: string) => {
    setLocalSelectedKeys(prev => prev.filter(key => key !== itemKey))
  }, [])

  const handleAddAll = useCallback(() => {
    const newKeys = availableItemsFiltered.map(item => item.key)
    setLocalSelectedKeys(prev => [...prev, ...newKeys])
  }, [availableItemsFiltered])

  const handleRemoveAll = useCallback(() => {
    setLocalSelectedKeys([])
  }, [])

  useEffect(() => {
    const wasOpened = !prevVisibleRef.current && visible
    prevVisibleRef.current = visible

    if (wasOpened) {
      setLocalSelectedKeys(selectedItemKeys)
      setSearchAvailable('')
      setSearchSelected('')
    }
  }, [visible, selectedItemKeys])

  useEffect(() => {
    if (!visible) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'ArrowRight') {
        e.preventDefault()
        handleAddAll()
      }
      if (e.ctrlKey && e.key === 'ArrowLeft') {
        e.preventDefault()
        handleRemoveAll()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [visible, handleAddAll, handleRemoveAll])

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      setLocalSelectedKeys(items => {
        const oldIndex = items.indexOf(active.id as string)
        const newIndex = items.indexOf(over.id as string)
        return arrayMove(items, oldIndex, newIndex)
      })
    }

    setActiveId(null)
  }

  const handleOk = () => {
    onOk(localSelectedKeys)
  }

  const activeItem = activeId ? allItemsMap.get(activeId) : null

  return (
    <Modal
      title={title}
      open={visible}
      onOk={handleOk}
      onCancel={onCancel}
      width={width}
      okText="Сохранить"
      cancelText="Отмена"
      okButtonProps={{ disabled: loading }}
      cancelButtonProps={{ disabled: loading }}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin size="large" tip="Загрузка..." />
        </div>
      ) : (
        <ModalContent>
          <ItemList>
            <ListHeader>
              <HeaderTitle>{leftPanelTitle}</HeaderTitle>
              <HeaderCounter color="default">{availableItemsFiltered.length}</HeaderCounter>
            </ListHeader>
            {showSearch && (
              <SearchWrapper>
                <Search
                  placeholder="Поиск..."
                  value={searchAvailable}
                  onChange={e => setSearchAvailable(e.target.value)}
                  prefix={<SearchOutlined />}
                  allowClear
                />
              </SearchWrapper>
            )}
            <ListBody>
              {availableItemsFiltered.length === 0 ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={searchAvailable ? 'Ничего не найдено' : emptyLeftText}
                  style={{ marginTop: 60 }}
                />
              ) : (
                availableItemsFiltered.map(item => (
                  <AvailableItemComponent
                    key={item.key}
                    item={item}
                    onAdd={() => handleAddItem(item.key)}
                  />
                ))
              )}
            </ListBody>
          </ItemList>

          <ActionButtons>
            <Button
              icon={<ArrowRightOutlined />}
              onClick={handleAddAll}
              disabled={availableItemsFiltered.length === 0}
              title="Добавить все (Ctrl+→)"
            />
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={handleRemoveAll}
              disabled={localSelectedKeys.length === 0}
              title="Удалить все (Ctrl+←)"
            />
          </ActionButtons>

          <ItemList $isTarget>
            <ListHeader>
              <Space size={8} align="center">
                <HeaderTitle>{rightPanelTitle}</HeaderTitle>
                {selectedItems.length > 0 && (
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    (перетащите для изменения порядка)
                  </Text>
                )}
              </Space>
              <HeaderCounter color="blue">{selectedItems.length}</HeaderCounter>
            </ListHeader>
            {showSearch && (
              <SearchWrapper>
                <Search
                  placeholder="Поиск..."
                  value={searchSelected}
                  onChange={e => setSearchSelected(e.target.value)}
                  prefix={<SearchOutlined />}
                  allowClear
                />
              </SearchWrapper>
            )}
            <ListBody>
              {selectedItems.length === 0 ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <Space direction="vertical" size={4}>
                      <Text>{emptyRightText}</Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {emptyRightDescription}
                      </Text>
                    </Space>
                  }
                  style={{ marginTop: 60 }}
                />
              ) : searchSelected && selectedItems.length === 0 ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Ничего не найдено"
                  style={{ marginTop: 60 }}
                />
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={selectedItems.map(item => item.key)}
                    strategy={verticalListSortingStrategy}
                  >
                    {selectedItems.map((item, index) => (
                      <SortableItemComponent
                        key={item.key}
                        item={item}
                        index={index}
                        onRemove={() => handleRemoveItem(item.key)}
                      />
                    ))}
                  </SortableContext>
                  <DragOverlay>
                    {activeItem && (
                      <ItemCard $isDragging>
                        <DragHandle>
                          <HolderOutlined />
                        </DragHandle>
                        <SortOrderBadge color="blue">
                          {localSelectedKeys.indexOf(activeItem.key) + 1}
                        </SortOrderBadge>
                        <ItemContent>
                          <ItemTitle>{activeItem.title}</ItemTitle>
                          {activeItem.description && (
                            <ItemDescription>{activeItem.description}</ItemDescription>
                          )}
                        </ItemContent>
                      </ItemCard>
                    )}
                  </DragOverlay>
                </DndContext>
              )}
            </ListBody>
          </ItemList>
        </ModalContent>
      )}
    </Modal>
  )
}

